/**
 * Pushes an event to the dataLayer for Google Tag Manager
 *
 * @param {string} eventName - The name of the event to track
 * @param {Object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventParams,
    });
  } else {
    console.warn(
      'DataLayer not available. Event not tracked:',
      eventName,
      eventParams
    );
  }
};

/**
 * Track a product view event
 *
 * @param {Object} product - The product data
 */
export const trackProductView = product => {
  window.dataLayer.push({ ecommerce: null });

  const price = product.attributes?.price?.amount / 100 || 0;
  const currency = product.attributes?.price?.currency || 'SEK';

  trackEvent('view_item', {
    ecommerce: {
      // Both are read at this level: Meta's ViewContent tag resolves
      // {{dlv - ecommerce.value}}, and GA4 ignores a value with no currency.
      value: price,
      currency,
      items: [
        {
          item_id: product.id?.uuid || product.id,
          item_name: product.attributes?.title || 'Unknown Product',
          price,
          currency,
          item_brand: product.attributes?.publicData?.brand || '',
          item_category: product.attributes?.publicData?.categoryName || '',
          item_designer: product.attributes?.publicData?.designer || '',
        },
      ],
    },
  });
};

/**
 * Track when user begins checkout
 *
 * @param {Array} products - Array of products in the checkout
 */
export const trackBeginCheckout = product => {
  const lineItems = product.lineItems || [];

  let quantity = 1;
  if (Array.isArray(lineItems)) {
    lineItems.forEach(line => {
      if (line && line.code === 'line-item/item') {
        quantity = parseInt(line.quantity.d[0], 10) || 1;
      }
    });
  }

  const items = [
    {
      item_id: product.listing?.id?.uuid || product.listing?.id,
      item_name: product.listing.attributes?.title || 'Unknown Product',
      price: product.listing.attributes?.price?.amount / 100 || 0,
      quantity: quantity || 1,
      currency: product.listing.attributes?.price?.currency || 'SEK',
      item_brand: product.listing.attributes?.publicData?.brand || '',
      item_designer: product.listing.attributes?.publicData?.designer || '',
    },
  ];

  const value = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  trackEvent('begin_checkout', {
    ecommerce: {
      items,
      value,
      currency: items[0]?.currency || 'SEK',
    },
  });
};

/**
 * Push a GA4 purchase event. Used for card payments (trackPurchase) and for
 * Klarna payments when the buyer returns from Klarna.
 *
 * @param {Object} params
 * @param {string} params.transactionId
 * @param {number} params.value what the buyer paid, in major units: items minus
 *   any voucher discount, plus shipping. Meta and Google Ads use it as the
 *   conversion value.
 * @param {string} params.currency
 * @param {number} params.shipping in major units
 * @param {Array} params.items GA4 items
 */
export const pushPurchaseEvent = ({ transactionId, value, currency, shipping = 0, items }) => {
  trackEvent('purchase', {
    ecommerce: {
      transaction_id: transactionId,
      value,
      tax: 0,
      shipping,
      currency,
      items,
    },
  });
};

/**
 * Track a purchase completion event
 *
 * @param {Object} orderData - Data about the completed order
 * @param {Object} paidTotal - the transaction's payinTotal (Money), i.e. what
 *   the buyer paid. Falls back to listing price x quantity + shipping.
 */
export const trackPurchase = (orderId, orderData, listing, currentUser, paidTotal) => {
  // Clear previous ecommerce object to prevent data leakage
  window.dataLayer?.push({ ecommerce: null });

  // --- Enhanced Conversion Data ---
  if (currentUser) {
    const email = currentUser.attributes?.email;

    window.dataLayer.push({
      event: 'enhanced_conversion',
      enhanced_conversion_data: {
        email: email || undefined,
        user_id: currentUser.id?.uuid || currentUser.id || undefined,
      },
    });
  }

  const items = [
    {
      item_id: listing.id?.uuid || listing.id,
      item_name: listing.attributes?.title || 'Unknown Product',
      price: listing.attributes?.price?.amount / 100 || 0,
      quantity: orderData?.quantity || 1,
      currency: listing.attributes?.price?.currency || 'SEK',
      item_brand: listing.attributes?.publicData?.brand || '',
      item_designer: listing.attributes?.publicData?.designer || '',
    },
  ];

  const subtotal = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const shippingPrice =
    orderData?.protectedData?.deliveryMethod === 'shipping'
      ? orderData?.shippingPrice / 100 || 0
      : 0;

  // What the buyer actually paid. Meta and Google Ads both treat this as the
  // conversion value, so shipping has to be part of it, and a voucher discount
  // has to come off it. The transaction's payinTotal has both; the listing price
  // is only a fallback.
  const value =
    typeof paidTotal?.amount === 'number' ? paidTotal.amount / 100 : subtotal + shippingPrice;

  pushPurchaseEvent({
    transactionId: orderId?.uuid || orderId,
    value,
    currency: paidTotal?.currency || items[0]?.currency || 'SEK',
    shipping: shippingPrice,
    items,
  });
};

/**
 * Track when a user creates account
 *
 */
export const trackCreateAccount = () => {
  trackEvent('sign_up', {
    method: 'direct',
    user_type: 'customer',
  });
};

/**
 * Track when a user signs up for newsletter
 *
 */
export const trackNewsletterSignup = () => {
  trackEvent('newsletter_signup', {
    method: 'footer_form',
  });
};

/**
 * Track when a user starts creating a listing
 *
 */
export const trackStartListing = () => {
  trackEvent('generate_lead', {
    method: 'create_listing',
  });
};

/**
 * Track a visit that stayed on site past the engagement threshold
 *
 * @param {number} seconds - The threshold that was reached
 */
export const trackEngagedVisit = seconds => {
  trackEvent('engaged_visit', {
    engagement_time_seconds: seconds,
  });
};

/**
 * Track when a user adds a product to their wishlist
 *
 * @param {Object} listing - The listing/product object
 */
export const trackAddToWishlist = listing => {
  const price = listing.attributes?.price?.amount / 100 || 0;
  const currency = listing.attributes?.price?.currency || 'SEK';

  trackEvent('add_to_wishlist', {
    ecommerce: {
      value: price,
      // Without this GA4 discards the value above.
      currency,
      items: [
        {
          item_id: listing.id?.uuid || listing.id,
          item_name: listing.attributes?.title || 'Unknown Product',
          price,
          currency,
          item_brand: listing.attributes?.publicData?.brand || '',
          item_designer: listing.attributes?.publicData?.designer || '',
        },
      ],
    },
    method: 'product_page',
  });
};
